import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';

import { Store } from '@ngrx/store';
import type { Observable } from 'rxjs';
import { take } from 'rxjs';
import { map } from 'rxjs/operators';

import { BillingPlanTier } from '@core/billing-types';
import { filterBoolean } from '@core/common-legacy';
import { FeatureSpecSelectors } from '@core/feature-spec-data';
import { FeatureCode } from '@core/feature-spec-types';
import { I18nService } from '@core/i18n-legacy';
import { DialogService, DialogWidth } from '@core/user-interface-legacy';

import { FeatureGuardDialogComponent } from './feature-guard-dialog/feature-guard-dialog.component';
import type { UpsellBadgeConfig } from './feature-guard.types';
import { FeatureGuardMode, GuardedFeature } from './feature-guard.types';

@Injectable({ providedIn: 'root' })
/**
 * @deprecated use FeatureSpecFacade from @recruitee/feature-spec-logic instead
 */
export class FeatureGuardFacade {
  private readonly dialogService = inject(DialogService);
  private readonly router = inject(Router);
  private readonly store = inject(Store);
  private readonly i18n = inject(I18nService);

  public showFeatureInfo(
    mode: FeatureGuardMode,
    feature: GuardedFeature,
    availableFromPlanOverwrite?: BillingPlanTier,
  ) {
    switch (mode) {
      case FeatureGuardMode.Dialog:
        this.showDialog(feature, availableFromPlanOverwrite);
        break;
      case FeatureGuardMode.Redirect:
        this.redirectToEmptyState(feature);
        break;
      default:
    }
  }

  public showDialog(feature: GuardedFeature, availableFromPlanOverwrite?: BillingPlanTier) {
    this.dialogService.openCustom(
      FeatureGuardDialogComponent,
      {
        width: DialogWidth.Small,
      },
      { feature, availableFromPlanOverwrite },
    );
  }

  public redirectToEmptyState(feature: GuardedFeature) {
    switch (feature) {
      case GuardedFeature.SendLater:
        this.router.navigate([`mailbox/upgrade`]);
        break;
      default:
    }
  }

  /**
   * @deprecated use FeatureSpecFacade from @recruitee/feature-spec-logic instead
   */
  public checkFeatureAvailability(feature: GuardedFeature): Observable<boolean> {
    const featureCode = this.getFeatureCodeFromGuard(feature);

    return this.store.select(FeatureSpecSelectors.forCode(featureCode)).pipe(
      filterBoolean,
      map(entity => entity.entitled),
    );
  }

  public getFeatureCodeFromGuard(feature: GuardedFeature): FeatureCode {
    return feature;
  }

  public getPlanWhereFeatureIsAvailable(
    feature: GuardedFeature,
    checkWithPlan: BillingPlanTier | false = false,
  ): Observable<BillingPlanTier | null> {
    const featureCode = this.getFeatureCodeFromGuard(feature);

    if (checkWithPlan) {
      throw new Error(`checkWithPlan is not supported in new config`);
    }
    return this.store.select(FeatureSpecSelectors.forCode(featureCode)).pipe(
      filterBoolean,
      take(1), // TODO - remove take(1) when consumers start utilizing reactivity
      map(entity => entity.availableFrom),
    );
  }

  public getFeaturePlanAvailabilityMap$() {
    return this.store.select(FeatureSpecSelectors.asMap).pipe(filterBoolean);
  }

  public getUpsellDefs(): Partial<Record<BillingPlanTier, UpsellBadgeConfig>> {
    return {
      [BillingPlanTier.Scale]: {
        color: 'scale-reverse',
        label: this.i18n.translate('app.pricing.scale'),
        tooltip: this.i18n.translate('app.pricing.feature_guard.feature_available_scale'),
      },
      [BillingPlanTier.Optimize]: {
        color: 'optimize',
        label: this.i18n.translate('app.pricing.optimize'),
        tooltip: this.i18n.translate('app.pricing.feature_guard.feature_available_optimize'),
      },
      [BillingPlanTier.Grow]: {
        color: 'grow',
        label: this.i18n.translate('app.pricing.grow'),
        tooltip: this.i18n.translate('app.pricing.feature_guard.feature_available_grow'),
      },
    };
  }
}
